import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import DocumentPreview from '../BCP/DocumentPreview';
import { useAdminHeaderActions } from '../../contexts/AdminHeaderActionsContext';

interface BCPItem {
  id: number;
  business_name: string;
  business_type: string;
  industry: string;
  status: string;
  employee_count: number;
  city: string;
  state: string;
  country: string;
  document_url: string | null;
  ai_analysis: boolean;
  user_id: number;
  user_email: string;
  user_name: string;
  created_at: string;
  updated_at: string;
}

const BCPManagement: React.FC = () => {
  const { setActions } = useAdminHeaderActions();
  const [bcps, setBcps] = useState<BCPItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'with_document' | 'without_document'>('all');
  const [showPreview, setShowPreview] = useState(false);
  const [previewBcpId, setPreviewBcpId] = useState<number | null>(null);
  const [previewBcpName, setPreviewBcpName] = useState<string>('');

  const loadBCPs = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/v1/admin/bcps/');
      setBcps(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load BCPs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBCPs();
  }, [loadBCPs]);

  useEffect(() => {
    setActions(
      <>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="all">All BCPs ({bcps.length})</option>
          <option value="with_document">With Document ({bcps.filter((b) => b.document_url).length})</option>
          <option value="without_document">Without Document ({bcps.filter((b) => !b.document_url).length})</option>
        </select>
        <button
          type="button"
          onClick={loadBCPs}
          className="px-4 py-2 text-sm font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700"
        >
          Refresh
        </button>
      </>
    );
    return () => setActions(null);
  }, [bcps, filter, loadBCPs, setActions]);

  const handlePreview = (bcp: BCPItem) => {
    setPreviewBcpId(bcp.id);
    setPreviewBcpName(bcp.business_name);
    setShowPreview(true);
  };

  const handleDownload = async (bcp: BCPItem) => {
    try {
      const response = await api.get(`/v1/admin/bcps/${bcp.id}/document`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bcp_${bcp.business_name.replace(/\s+/g, '_')}_${bcp.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to download document');
    }
  };

  const filteredBcps = bcps.filter((bcp) => {
    if (filter === 'with_document') return bcp.document_url;
    if (filter === 'without_document') return !bcp.document_url;
    return true;
  });

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      archived: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading BCP plans...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button
          onClick={loadBCPs}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">BCP Plans Management</h2>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Business
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Owner
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                AI Analysis
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Document
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredBcps.map((bcp) => (
              <tr key={bcp.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{bcp.business_name}</div>
                  <div className="text-sm text-gray-500">
                    {bcp.industry} • {bcp.employee_count} employees
                  </div>
                  <div className="text-xs text-gray-400">
                    {bcp.city}, {bcp.state}, {bcp.country}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{bcp.user_name || 'N/A'}</div>
                  <div className="text-sm text-gray-500">{bcp.user_email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(bcp.status)}`}>
                    {bcp.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {bcp.ai_analysis ? (
                    <span className="text-green-600 text-sm">✓ Complete</span>
                  ) : (
                    <span className="text-gray-400 text-sm">—</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {bcp.document_url ? (
                    <span className="text-green-600 text-sm">✓ Generated</span>
                  ) : (
                    <span className="text-gray-400 text-sm">—</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(bcp.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                  <button
                    onClick={() => handlePreview(bcp)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Preview
                  </button>
                  {bcp.document_url && (
                    <button
                      onClick={() => handleDownload(bcp)}
                      className="text-green-600 hover:text-green-900"
                    >
                      Download PDF
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredBcps.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No BCP plans found matching the filter.
          </div>
        )}
      </div>

      {/* Document Preview Modal - uses same component as user frontend */}
      {showPreview && previewBcpId && (
        <DocumentPreview
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          bcpId={previewBcpId}
          bcpName={previewBcpName}
          isAdmin={true}
        />
      )}
    </div>
  );
};

export default BCPManagement;
